import { CameraManager, type CameraInfo } from "./camera";
import { getWasmUrl, prepareDecoder, type DecodeResult } from "./decoder";
import { DuplicateFilter } from "./dupfilter";
import { filterAllowedSymbologies } from "./entitlements";
import { beep, vibrate } from "./feedback";
import type { Symbology } from "./formats";
import { createPipelineState, runPipeline, type PipelineOutcome } from "./pipeline";
import { resolveWorkerFactory } from "./runtime";
import { getActiveReporter } from "./usage";
import { WorkerDecoder } from "./worker-client";

export interface ScannerOptions {
  symbologies?: Symbology[];
  /** Ignore re-reads of the same code within this window (ms). 0 disables. */
  duplicateFilter?: number;
  playSoundOnScan?: boolean;
  vibrateOnScan?: boolean;
  /** Downscale the decoded region only if it exceeds this width. */
  maxDecodeWidth?: number;
  /**
   * Region of interest as fractions of the frame. Decoded at NATIVE camera
   * resolution — small printed barcodes need every pixel of density.
   */
  roi?: { x: number; y: number; width: number; height: number };
  /**
   * Apply ~2x sensor zoom on cameras that support it. Raises px/module and
   * avoids minimum-focus-distance blur on close barcodes. Default true.
   */
  autoZoom?: boolean;
  /**
   * Switch the torch on automatically when frames are too dark to decode
   * (dark scenes cause long exposures → motion blur). Default true.
   */
  autoTorch?: boolean;
}

export interface ScanEvent {
  barcodes: DecodeResult[];
  /** Decode latency for this frame in ms. */
  decodeTimeMs: number;
}

export interface ScannerStats {
  fps: number;
  avgDecodeMs: number;
  framesDecoded: number;
}

export interface ScanHint {
  /**
   * move-closer: bars spotted but undecodable from this distance.
   * low-light: frames too dark to decode reliably; add light.
   * torch-enabled: scene was dark and the torch was switched on automatically.
   */
  type: "move-closer" | "low-light" | "torch-enabled";
}

type Events = {
  scan: (e: ScanEvent) => void;
  stats: (s: ScannerStats) => void;
  error: (err: Error) => void;
  hint: (h: ScanHint) => void;
};

/** Fast-path decode width: ~8x cheaper than thorough at 1600 (benchmarked). */
const FAST_DECODE_WIDTH = 1000;

/**
 * Bars sighted this many consecutive frames with zero successful reads means
 * the code is physically undecodable from here (too small / too blurred) —
 * verified on a real frame where 2 px/module resisted every software rescue.
 */
const HINT_AFTER_FAILED_SIGHTINGS = 8;

const DEFAULTS: Required<ScannerOptions> = {
  symbologies: ["ean13", "code128"],
  duplicateFilter: 3000,
  playSoundOnScan: true,
  vibrateOnScan: true,
  maxDecodeWidth: 2048,
  roi: { x: 0.08, y: 0.2, width: 0.84, height: 0.6 },
  autoZoom: true,
  autoTorch: true,
};

/** Below this mean luma, exposure stretches and motion blur kills decoding
 * (verified on a real dim-room frame that resisted every software rescue). */
const DARK_LUMA_THRESHOLD = 70;
const DARK_FRAMES_BEFORE_ACTION = 8;

export class ScannerView {
  private opts: Required<ScannerOptions>;
  private camera: CameraManager;
  private overlay: HTMLCanvasElement;
  private grabCanvas = document.createElement("canvas");
  private listeners: { [K in keyof Events]: Events[K][] } = { scan: [], stats: [], error: [], hint: [] };
  private lastHintAt = 0;
  private darkFrames = 0;
  private torchAutoEnabled = false;
  private exposureBoosted = false;
  private workerDecoder: WorkerDecoder | null = null;
  private inlineState = createPipelineState();
  private dupFilter: DuplicateFilter;
  private running = false;
  private paused = false;
  private decodeTimes: number[] = [];
  private frameCount = 0;
  private statsWindowStart = 0;

  private constructor(private root: HTMLElement, opts: ScannerOptions) {
    this.opts = { ...DEFAULTS, ...opts };
    this.opts.symbologies = filterAllowedSymbologies(this.opts.symbologies);
    this.dupFilter = new DuplicateFilter(this.opts.duplicateFilter);
    this.root.classList.add("barqscan-root");
    const video = document.createElement("video");
    this.overlay = document.createElement("canvas");
    Object.assign(this.root.style, { position: "relative", overflow: "hidden" });
    Object.assign(video.style, { width: "100%", height: "100%", objectFit: "cover", display: "block" });
    Object.assign(this.overlay.style, {
      position: "absolute", inset: "0", width: "100%", height: "100%", pointerEvents: "none",
    });
    this.root.append(video, this.overlay);
    this.camera = new CameraManager(video);
  }

  static async create(element: HTMLElement, opts: ScannerOptions = {}): Promise<ScannerView> {
    const view = new ScannerView(element, opts);
    // Try the worker first — inline decode stays as the universal fallback.
    const factory = resolveWorkerFactory();
    if (factory) {
      view.workerDecoder = await WorkerDecoder.create(factory, getWasmUrl());
    }
    if (!view.workerDecoder) await prepareDecoder();
    await view.camera.start(undefined, { autoZoom: view.opts.autoZoom });
    view.running = true;
    view.statsWindowStart = performance.now();
    view.loop();
    return view;
  }

  on<K extends keyof Events>(event: K, cb: Events[K]): void {
    this.listeners[event].push(cb);
  }

  private emit<K extends keyof Events>(event: K, arg: Parameters<Events[K]>[0]): void {
    for (const cb of this.listeners[event]) (cb as (a: typeof arg) => void)(arg);
  }

  pause(): void { this.paused = true; }
  resume(): void { this.paused = false; }

  destroy(): void {
    this.running = false;
    getActiveReporter()?.flush(); // deliver pending scan counts as soon as scanning stops
    this.workerDecoder?.terminate();
    this.workerDecoder = null;
    this.camera.stop();
    this.root.replaceChildren();
  }

  listCameras(): Promise<CameraInfo[]> { return this.camera.listCameras(); }
  async switchCamera(deviceId: string): Promise<void> {
    await this.camera.start(deviceId, { autoZoom: this.opts.autoZoom });
  }
  get torchSupported(): boolean { return this.camera.torchSupported; }
  setTorch(on: boolean): Promise<void> { return this.camera.setTorch(on); }
  get zoomRange(): { min: number; max: number; step: number } | null {
    return this.camera.zoomRange;
  }
  setZoom(zoom: number): Promise<void> { return this.camera.setZoom(zoom); }

  setSymbologies(symbologies: Symbology[]): void {
    this.opts.symbologies = filterAllowedSymbologies(symbologies);
  }

  /**
   * Serial decode loop, two-tier cadence: a cheap fast-path attempt every
   * frame (~8x cheaper, measured), plus a thorough attempt (tryHarder +
   * rotation + inversion, full native width) every third frame for the hard
   * cases. Maximizes attempts/second, which is what time-to-read is made of.
   */
  private async loop(): Promise<void> {
    const video = this.camera.video;
    let frameNo = 0;
    while (this.running) {
      if (this.paused || video.readyState < 2) {
        await nextFrame(video);
        continue;
      }
      try {
        // The locator rescue covers localization; the pricey full-frame
        // thorough pass only adds rotation/inversion coverage now, so it
        // runs on a sparser cadence to keep attempts-per-second high.
        const thorough = frameNo++ % 3 === 2;
        const image = this.grabFrame(video, thorough ? this.opts.maxDecodeWidth : FAST_DECODE_WIDTH);
        const t0 = performance.now();
        let results: DecodeResult[] = [];
        if (image) {
          const outcome = await this.runPipeline(image, thorough);
          results = outcome.results;
          if (results.length > 0) {
            this.failedSightings = 0;
            this.darkFrames = 0;
          } else if (!outcome.skippedBlurry) {
            await this.handleDarkness(outcome.luma);
            if (outcome.sawRegions) {
              if (++this.failedSightings >= HINT_AFTER_FAILED_SIGHTINGS) {
                this.emitHint({ type: "move-closer" });
              }
            } else {
              this.failedSightings = 0;
            }
          }
        }
        const decodeTimeMs = performance.now() - t0;
        this.trackStats(decodeTimeMs);
        const fresh = this.dupFilter.filter(results, performance.now());
        if (fresh.length > 0) {
          this.drawHighlights(fresh, video);
          if (this.opts.playSoundOnScan) beep();
          if (this.opts.vibrateOnScan) vibrate();
          const reporter = getActiveReporter();
          if (reporter) {
            for (const b of fresh) reporter.countScan(1, b.symbology);
          }
          this.emit("scan", { barcodes: fresh, decodeTimeMs });
        } else {
          this.clearHighlights();
        }
      } catch (err) {
        this.emit("error", err instanceof Error ? err : new Error(String(err)));
      }
      await nextFrame(video);
    }
  }

  /** Source rect of the last grab in native video coords, plus its scale. */
  private grabMeta = { sx: 0, sy: 0, scale: 1 };
  private lastFrame: ImageData | null = null;

  private grabFrame(video: HTMLVideoElement, maxWidth: number): ImageData | null {
    const vw = video.videoWidth, vh = video.videoHeight;
    if (!vw || !vh) return null;
    const { roi } = this.opts;
    const sx = Math.round(vw * roi.x), sy = Math.round(vh * roi.y);
    const sw = Math.round(vw * roi.width), sh = Math.round(vh * roi.height);
    // Native resolution by default; downscale only oversized crops.
    const scale = Math.min(1, maxWidth / sw);
    const w = Math.round(sw * scale), h = Math.round(sh * scale);
    this.grabCanvas.width = w;
    this.grabCanvas.height = h;
    this.grabMeta = { sx, sy, scale };
    const ctx = this.grabCanvas.getContext("2d", { willReadFrequently: true })!;
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, w, h);
    const frame = ctx.getImageData(0, 0, w, h);
    // Debug snapshots keep the native-res (thorough) tier, not the fast tier.
    // Copy: the live frame's buffer is transferred to the decode worker.
    if (maxWidth === this.opts.maxDecodeWidth || !this.lastFrame) {
      this.lastFrame = {
        data: new Uint8ClampedArray(frame.data),
        width: w,
        height: h,
        colorSpace: "srgb",
      } as ImageData;
    }
    return frame;
  }

  private failedSightings = 0;

  /** Worker-first pipeline; any worker failure downgrades to inline for good. */
  private async runPipeline(image: ImageData, thorough: boolean): Promise<PipelineOutcome> {
    if (this.workerDecoder) {
      const outcome = await this.workerDecoder.run(image, this.opts.symbologies, thorough);
      if (outcome) return outcome;
      // Worker died — terminate and fall back to inline decoding.
      this.workerDecoder.terminate();
      this.workerDecoder = null;
      await prepareDecoder();
      return { results: [], sawRegions: false, luma: 128, skippedBlurry: true };
    }
    return runPipeline(image, this.opts.symbologies, thorough, this.inlineState);
  }

  /**
   * Dark scenes stretch exposure time and the motion blur wipes out bars
   * (verified on a real captured frame). Torch first, then an exposure
   * boost, hint as the last resort.
   */
  private async handleDarkness(luma: number): Promise<void> {
    if (luma >= DARK_LUMA_THRESHOLD) {
      this.darkFrames = 0;
      return;
    }
    if (++this.darkFrames < DARK_FRAMES_BEFORE_ACTION) return;

    if (this.opts.autoTorch && !this.torchAutoEnabled && this.camera.torchSupported) {
      this.torchAutoEnabled = true;
      this.darkFrames = 0;
      try {
        await this.camera.setTorch(true);
        this.emitHint({ type: "torch-enabled" });
        return;
      } catch {
        // fall through
      }
    }
    if (!this.exposureBoosted) {
      this.exposureBoosted = true;
      this.darkFrames = 0;
      if (await this.camera.boostExposure()) return;
    }
    this.emitHint({ type: "low-light" });
    this.darkFrames = 0;
  }

  private emitHint(hint: ScanHint): void {
    const now = performance.now();
    if (now - this.lastHintAt < 1500) return;
    this.lastHintAt = now;
    this.emit("hint", hint);
  }

  /** Exactly what the decoder saw last frame — for debugging field failures. */
  getDebugFrame(): { image: ImageData; camera: { width: number; height: number } } | null {
    if (!this.lastFrame) return null;
    return { image: this.lastFrame, camera: this.camera.resolution };
  }

  private drawHighlights(results: DecodeResult[], video: HTMLVideoElement): void {
    const ctx = this.overlay.getContext("2d")!;
    this.overlay.width = this.overlay.clientWidth;
    this.overlay.height = this.overlay.clientHeight;
    // decode-image coords -> native video coords -> overlay (object-fit: cover).
    const { sx, sy, scale: grabScale } = this.grabMeta;
    const vw = video.videoWidth, vh = video.videoHeight;
    const cover = Math.max(this.overlay.width / vw, this.overlay.height / vh);
    const dx = (this.overlay.width - vw * cover) / 2;
    const dy = (this.overlay.height - vh * cover) / 2;
    ctx.strokeStyle = "#22c55e";
    ctx.lineWidth = 3;
    for (const r of results) {
      ctx.beginPath();
      r.points.forEach((p, i) => {
        const x = (sx + p.x / grabScale) * cover + dx;
        const y = (sy + p.y / grabScale) * cover + dy;
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      });
      ctx.closePath();
      ctx.stroke();
    }
  }

  private clearHighlights(): void {
    const ctx = this.overlay.getContext("2d");
    ctx?.clearRect(0, 0, this.overlay.width, this.overlay.height);
  }

  private trackStats(decodeMs: number): void {
    this.decodeTimes.push(decodeMs);
    this.frameCount++;
    const now = performance.now();
    const elapsed = now - this.statsWindowStart;
    if (elapsed >= 1000) {
      const avg = this.decodeTimes.reduce((a, b) => a + b, 0) / this.decodeTimes.length;
      this.emit("stats", {
        fps: (this.decodeTimes.length / elapsed) * 1000,
        avgDecodeMs: avg,
        framesDecoded: this.frameCount,
      });
      this.decodeTimes = [];
      this.statsWindowStart = now;
    }
  }
}

function nextFrame(video: HTMLVideoElement): Promise<void> {
  return new Promise((resolve) => {
    if ("requestVideoFrameCallback" in video) {
      video.requestVideoFrameCallback(() => resolve());
    } else {
      requestAnimationFrame(() => resolve());
    }
  });
}
